import { Product } from "@/api/product-api";
import { useCurrency } from "@/hooks/useCurrency";
import { useProductPrice } from "@/hooks/useProductPrice";
import { cn } from "@/libs/utils";
import { useLocale } from "@/store/locale-store";
import { ProductUtil } from "@/utils/ProductUtil";
import { useTrans } from "@/utils/useTrans";
import { GePrice } from "@/v2-components/GePrice";
import { QuantityCounter } from "@/v2-components/cart-sheet/quantity-counter";
import { AspectRatio } from "@/v2-components/ui/aspect-ratio";
import Image from "next/image";

interface Props {
  product: Product;
  quantity: number;
  maxQuantity?: number;
  selected: boolean;
  onSelect: () => void;
  onQuantityChange: (quantity: number) => void;
}

export function AccessoryItem({
  product,
  quantity,
  maxQuantity = 4,
  selected,
  onSelect,
  onQuantityChange,
}: Props) {
  const { t } = useTrans(["gnb"]);
  const currency = useCurrency();
  const { countryCode } = useLocale();
  const price = ProductUtil.getProductPrice({ currency, countryCode, product });
  const productImages = ProductUtil.getProductImages({ product });
  const { displayOriginalPrice, displayDiscountAmount, displaySalePrice } =
    useProductPrice(price);

  if (!product || !price) {
    return null;
  }

  return (
    <li
      className={cn(
        "mt-3 flex select-none items-center rounded-lg border border-coolgray-20 py-5 pl-2.5 pr-5 transition-colors hover:border-coolgray-40",
        selected && "outline outline-2 -outline-offset-2 outline-blue-60",
      )}
      onClick={() => onSelect()}
    >
      <div className="flex w-[114px] flex-col items-center">
        <AspectRatio ratio={1}>
          <Image
            src={productImages[0]}
            alt={product.name}
            className="h-full w-full rounded-md object-cover"
            sizes="114px"
            fill
          />
        </AspectRatio>
        <QuantityCounter
          value={quantity}
          decreaseDisabled={quantity <= 1}
          increaseDisabled={quantity >= maxQuantity}
          onDecrease={() => onQuantityChange(quantity - 1)}
          onIncrease={() => onQuantityChange(quantity + 1)}
          className={cn("laptop:hidden", selected ? "flex" : "hidden")}
        />
      </div>
      <div className="ml-4 flex flex-1 flex-col gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-lg font-600">{product.name}</p>
          {product.skuOptions[0].options.length > 0 && (
            <p className="text-base">{`Option: ${product.skuOptions[0].options.join(", ")}`}</p>
          )}
        </div>

        <p className="text-base">{t(product.shortDesc)}</p>
        <div className="flex items-center gap-1">
          <p className="text-lg font-600">
            <GePrice
              amount={displaySalePrice * quantity}
              currency={price.currency}
              countryCode={countryCode}
            />
          </p>
          {!!displayDiscountAmount && (
            <p className="text-lg text-coolgray-50 line-through">
              <GePrice
                amount={displayOriginalPrice * quantity}
                currency={price.currency}
                countryCode={countryCode}
              />
            </p>
          )}
        </div>
      </div>
      <QuantityCounter
        value={quantity}
        decreaseDisabled={quantity <= 0}
        increaseDisabled={quantity >= maxQuantity}
        onDecrease={() => {
          if (quantity === 1) {
            onSelect();
          } else {
            onQuantityChange(quantity - 1);
          }
        }}
        onIncrease={() => onQuantityChange(quantity + 1)}
        className={cn(
          "ml-5 hidden laptop:flex",
          selected ? "visible" : "invisible",
        )}
      />
    </li>
  );
}
